import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DevicesOtherOutlinedIcon from '@material-ui/icons/DevicesOtherOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';

import Container from '../Elements/Container';
import Question from '../FAQ/Question';
import Section from '../FAQ/Section';
import Video from '../FAQ/Video';
import TopNav from '../common/TopNav';

const useStyles = makeStyles(theme => ({
  page: {
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  pageClosed: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  hero: {
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(4),
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    background: 'linear-gradient(130deg, #005f9f 0%, #0d88c9 52%, #31b8e8 100%)',
    boxShadow: '0 26px 60px rgba(12, 67, 96, 0.18)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -100,
      right: -60,
      width: 260,
      height: 260,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.08)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -120,
      left: -40,
      width: 220,
      height: 220,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.06)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5),
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroClosed: {
    marginBottom: theme.spacing(1),
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
  },
  heroTitle: {
    maxWidth: 620,
    marginBottom: theme.spacing(1.5),
    fontSize: 37,
    lineHeight: 1.08,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
      fontSize: 30,
    },
  },
  heroText: {
    marginBottom: theme.spacing(8),
    maxWidth: 620,
    fontSize: 15,
    lineHeight: 1.85,
    color: 'rgba(255,255,255,0.82)',
  },
  layout: {
    alignItems: 'flex-start',
  },
  layoutClosed: {
    flex: 1,
    minHeight: 0,
    alignItems: 'stretch',
  },
  mainColumn: {
    [theme.breakpoints.down('sm')]: {
      order: 2,
    },
  },
  mainColumnClosed: {
    display: 'flex',
    minHeight: 0,
  },
  sideColumn: {
    [theme.breakpoints.down('sm')]: {
      order: 1,
    },
  },
  sideColumnClosed: {
    display: 'flex',
    minHeight: 0,
  },
  sectionList: {
    width: '100%',
    paddingRight: 0,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  sectionListClosed: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minHeight: 0,
    '& > *:last-child': {
      marginBottom: 0,
    },
  },
  stickyColumn: {
    position: 'sticky',
    top: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      position: 'static',
    },
  },
  stickyColumnClosed: {
    height: '100%',
  },
  sideCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5, 1.5),
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    background:
      'linear-gradient(180deg, rgba(250, 253, 255, 0.95) 0%, rgba(241, 248, 252, 0.93) 100%)',
    border: '1px solid rgba(19, 95, 130, 0.12)',
    boxShadow: '0 10px 20px rgba(16, 73, 105, 0.045), 0 1px 0 rgba(209, 227, 238, 0.56)',
    alignSelf: 'start',
  },
  sideCardClosed: {
    height: '100%',
  },
  sideHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.1),
    marginBottom: theme.spacing(0.7),
  },
  sideTitleIcon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(37, 134, 188, 0.1)',
    border: '1px solid rgba(37, 134, 188, 0.14)',
  },
  sideTitle: {
    marginBottom: 0,
    fontSize: 19,
    lineHeight: 1.2,
    fontWeight: 700,
    color: '#124a68',
  },
  sideText: {
    marginBottom: theme.spacing(1.1),
    fontSize: 16,
    lineHeight: 1.6,
    color: 'rgba(18, 74, 104, 0.76)',
  },
  shortcutButton: {
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(0.95, 1.15),
    minHeight: 0,
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    textTransform: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(237, 246, 252, 0.98) 100%)',
    borderColor: 'rgba(37, 134, 188, 0.34)',
    boxShadow: '0 8px 16px rgba(16, 73, 105, 0.04), 0 1px 0 rgba(206, 225, 238, 0.62)',
    '&:hover': {
      background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(226, 241, 250, 1) 100%)',
      borderColor: 'rgba(37, 134, 188, 0.5)',
      boxShadow: '0 10px 20px rgba(16, 73, 105, 0.06), 0 1px 0 rgba(193, 218, 234, 0.74)',
    },
  },
  routeList: {
    display: 'grid',
    gap: theme.spacing(0.8),
  },
  answerText: {
    fontSize: 14,
    lineHeight: 1.8,
    color: 'rgba(18, 74, 104, 0.84)',
  },
  stepsList: {
    display: 'grid',
    gap: theme.spacing(1.15),
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.4),
  },
  stepNumber: {
    width: 30,
    height: 30,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 10px 20px rgba(37, 134, 188, 0.2)',
  },
  stepText: {
    paddingTop: theme.spacing(0.35),
  },
  infoBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.6, 1.8),
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    backgroundColor: 'rgba(37, 134, 188, 0.06)',
    border: '1px solid rgba(37, 134, 188, 0.14)',
  },
  infoBlockAccent: {
    backgroundColor: 'rgba(12, 49, 70, 0.04)',
    borderColor: 'rgba(12, 49, 70, 0.12)',
  },
  infoTitle: {
    marginBottom: theme.spacing(0.7),
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
    color: 'rgba(18, 74, 104, 0.7)',
  },
  infoLine: {
    fontSize: 14,
    lineHeight: 1.7,
    color: '#124a68',
  },
  answerGroup: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid rgba(19, 95, 130, 0.08)',
    '&:first-child': {
      marginTop: 0,
      paddingTop: theme.spacing(0.5),
      borderTop: 'none',
    },
  },
  answerGroupTitle: {
    marginBottom: theme.spacing(1.2),
    fontSize: 18,
    fontWeight: 700,
    color: '#124a68',
  },
}));

const renderSteps = (classes, items) => (
  <Box className={classes.stepsList}>
    {items.map(({ number, text }) => (
      <Box key={`${number}-${String(text).slice(0, 20)}`} className={classes.step}>
        <Box className={classes.stepNumber}>{number}</Box>
        <Box className={`${classes.stepText} ${classes.answerText}`.trim()}>{text}</Box>
      </Box>
    ))}
  </Box>
);

const renderInfoBlock = (classes, title, lines, accent = false) => (
  <Box className={`${classes.infoBlock} ${accent ? classes.infoBlockAccent : ''}`.trim()}>
    {title && <Box className={classes.infoTitle}>{title}</Box>}
    {lines.map(line => (
      <Box key={String(line)} className={classes.infoLine}>
        {line}
      </Box>
    ))}
  </Box>
);

const Help = () => {
  const classes = useStyles();
  const pageRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [closedHeight, setClosedHeight] = useState(null);
  const hasExpandedItems = Object.keys(expandedItems).length > 0;

  useEffect(() => {
    const updateClosedLayout = () => {
      if (!pageRef.current) return;

      if (window.innerWidth <= 600) {
        setClosedHeight(null);
        return;
      }

      const rect = pageRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const availableHeight = Math.max(0, Math.floor(viewportHeight - rect.top - 14));
      setClosedHeight(availableHeight);
    };

    updateClosedLayout();
    window.addEventListener('resize', updateClosedLayout);

    return () => {
      window.removeEventListener('resize', updateClosedLayout);
    };
  }, [hasExpandedItems]);

  const toggleExpanded = id => {
    setExpandedItems(prev => (prev[id] ? {} : { [id]: true }));
  };

  const scrollToElement = elementId => {
    window.setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);
  };

  const openGuide = id => {
    setExpandedItems({ [id]: true });
    scrollToElement(`help-question-${id}`);
  };

  const faqSections = [
    {
      id: 'dispositivos',
      name: 'Dispositivos y estructura',
      description: 'Respuestas para vincular equipos y ordenar la instalacion dentro de Powermate.',
      badge: '2 preguntas frecuentes',
      icon: DevicesOtherOutlinedIcon,
      items: [
        {
          id: 103,
          indexLabel: '01',
          title: 'Como asocio un dispositivo a Powermate?',
          summary: 'Conexion inicial del equipo, acceso al portal de configuracion y alta final.',
          meta: '7 pasos / 1 video',
          content: (
            <>
              <Box className={classes.answerText}>
                Antes de iniciar, asegurate de que el equipo ya este instalado y energizado.
              </Box>
              <Box mt={2}>
                {renderSteps(classes, [
                  { number: 1, text: 'Energizar el dispositivo ya instalado.' },
                  { number: 2, text: 'Conectar el celular a la red WiFi emitida por el equipo.' },
                ])}
              </Box>
              {renderInfoBlock(classes, 'Datos de acceso WiFi', [
                'SSID: Powermeter_XXXXXXX',
                'Contrasena: powermeter',
              ])}
              <Box mt={2}>
                {renderSteps(classes, [{ number: 3, text: 'Ingresar al portal de configuracion del equipo.' }])}
              </Box>
              {renderInfoBlock(classes, 'Credenciales del portal', [
                'Usuario: admin',
                'Contrasena: powermeter',
              ])}
              <Box mt={2}>
                {renderSteps(classes, [
                  { number: 4, text: 'Abrir el Asistente de configuracion.' },
                  { number: 5, text: 'Cargar el mail de tu cuenta y un nombre amigable.' },
                  { number: 6, text: 'Seleccionar la red WiFi final e ingresar su contrasena.' },
                  { number: 7, text: 'Confirmar el alta del equipo desde el correo enviado.' },
                ])}
              </Box>
              <Video title='Video de asociacion del dispositivo' videoId='mhyMShDrJLo' />
            </>
          ),
        },
        {
          id: 104,
          indexLabel: '02',
          title: 'Cuales son los primeros pasos dentro de Powermate?',
          summary: 'Orden recomendado para crear la estructura y ubicar correctamente cada equipo.',
          meta: '5 pasos / 1 video',
          content: (
            <>
              <Box className={classes.answerText}>
                Powermate organiza la instalacion como un arbol jerarquico. Conviene cargar
                primero la estructura y despues ubicar el equipo fisico.
              </Box>
              <Box mt={2}>
                {renderSteps(classes, [
                  { number: 1, text: 'Entrar a Configuracion desde el icono superior.' },
                  { number: 2, text: 'Abrir la seccion Dispositivos dentro de Organizacion.' },
                  { number: 3, text: 'Crear una Empresa y luego una Zona.' },
                  { number: 4, text: 'Crear una Localizacion y asociar ahi el equipo fisico.' },
                  { number: 5, text: 'Guardar los cambios al cerrar cada nodo.' },
                ])}
              </Box>
              {renderInfoBlock(
                classes,
                'Como pensar la jerarquia',
                [
                  'Los niveles superiores sirven para ordenar la organizacion.',
                  'Los niveles inferiores reflejan la instalacion electrica real.',
                ],
                true
              )}
              <Video title='Video de primeros pasos' videoId='QJ0dsEScXTs' />
            </>
          ),
        },
      ],
    },
    {
      id: 'app',
      name: 'App y accesos directos',
      description: 'Instalacion de Powermate como app en celular o escritorio desde el navegador.',
      badge: '1 pregunta con 3 guias',
      icon: GetAppOutlinedIcon,
      items: [
        {
          id: 105,
          indexLabel: '03',
          title: 'Como descargo la app Powermate?',
          summary: 'Segun el dispositivo, el proceso cambia un poco. Aca tenes cada caso separado.',
          meta: 'Android / iOS / Desktop',
          content: (
            <>
              <Box className={classes.answerGroup}>
                <Box className={classes.answerGroupTitle}>Android</Box>
                {renderSteps(classes, [
                  { number: 1, text: 'Abrir powermate.powermeter.com.ar desde el celular.' },
                  { number: 2, text: 'Iniciar sesion con tu cuenta.' },
                  { number: 3, text: 'Abrir el menu del navegador y elegir Descargar app.' },
                  { number: 4, text: 'Confirmar la opcion Instalar.' },
                ])}
                <Video title='Video para Android' videoId='iripyNR5bCA' mobile />
              </Box>

              <Box className={classes.answerGroup}>
                <Box className={classes.answerGroupTitle}>iOS</Box>
                {renderSteps(classes, [
                  { number: 1, text: 'Abrir powermate.powermeter.com.ar desde Safari.' },
                  { number: 2, text: 'Iniciar sesion con tu cuenta.' },
                  { number: 3, text: 'Usar Compartir y luego Agregar a inicio.' },
                  { number: 4, text: 'Confirmar la opcion Agregar.' },
                ])}
                <Video title='Video para iOS' videoId='u6Jp7zfqQNE' mobile />
              </Box>

              <Box className={classes.answerGroup}>
                <Box className={classes.answerGroupTitle}>Desktop (Chrome)</Box>
                {renderSteps(classes, [
                  { number: 1, text: 'Abrir powermate.powermeter.com.ar e iniciar sesion.' },
                  { number: 2, text: 'Presionar el icono de instalar aplicacion en la barra.' },
                  { number: 3, text: 'Confirmar la opcion Instalar.' },
                ])}
                <Video title='Video para escritorio' videoId='x-d3G4hcf4I' />
              </Box>
            </>
          ),
        },
      ],
    },
  ];

  const featuredQuestions = [
    { label: 'Asociar dispositivo', id: 103 },
    { label: 'Instalar app', id: 105 },
    { label: 'Primeros pasos', id: 104 },
  ];

  return (
    <>
      <TopNav titulo='Ayuda' />
      <Container>
        <Box
          ref={pageRef}
          className={`${classes.page} ${!hasExpandedItems && closedHeight ? classes.pageClosed : ''}`.trim()}
          style={!hasExpandedItems && closedHeight ? { height: closedHeight, minHeight: closedHeight } : undefined}
        >
          <Box className={`${classes.hero} ${!hasExpandedItems && closedHeight ? classes.heroClosed : ''}`.trim()}>
            <Box className={classes.heroContent}>
              <Box className={classes.heroTitle}>Bienvenido a la seccion de ayuda de Powermate.</Box>
              <Box className={classes.heroText}>
                En esta pantalla encontraras respuestas claras y concisas a las preguntas mas
                comunes sobre el uso de Powermate. Cada pregunta incluye un video explicativo
                para que puedas resolver tus dudas de manera rapida y sencilla.
              </Box>
            </Box>
          </Box>

          <Grid
            container
            spacing={2}
            className={`${classes.layout} ${!hasExpandedItems && closedHeight ? classes.layoutClosed : ''}`.trim()}
          >
            <Grid
              item
              xs={12}
              md={8}
              className={`${classes.mainColumn} ${!hasExpandedItems && closedHeight ? classes.mainColumnClosed : ''}`.trim()}
            >
              <Box
                className={`${classes.sectionList} ${!hasExpandedItems && closedHeight ? classes.sectionListClosed : ''}`.trim()}
              >
                {faqSections.map(section => (
                  <Section
                    key={section.id}
                    id={section.id}
                    name={section.name}
                    description={section.description}
                    icon={section.icon}
                    badge={section.badge}
                    enabled
                  >
                    {section.items.map(item => (
                      <Question
                        key={item.id}
                        enabled
                        id={item.id}
                        title={item.title}
                        summary={item.summary}
                        meta={item.meta}
                        indexLabel={item.indexLabel}
                        isExpanded={!!expandedItems[item.id]}
                        toggle={toggleExpanded}
                      >
                        {item.content}
                      </Question>
                    ))}
                  </Section>
                ))}
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              className={`${classes.sideColumn} ${!hasExpandedItems && closedHeight ? classes.sideColumnClosed : ''}`.trim()}
            >
              <Box
                className={`${classes.stickyColumn} ${!hasExpandedItems && closedHeight ? classes.stickyColumnClosed : ''}`.trim()}
              >
                <Box
                  className={`${classes.sideCard} ${!hasExpandedItems && closedHeight ? classes.sideCardClosed : ''}`.trim()}
                >
                  <Box className={classes.sideHeader}>
                    <Box className={classes.sideTitleIcon}>
                      <MenuBookOutlinedIcon fontSize='small' />
                    </Box>
                    <Box className={classes.sideTitle}>Indice</Box>
                  </Box>
                  <Box className={classes.sideText}>
                    A continuacion encontraras accesos directos a las preguntas mas frecuentes para
                    que puedas navegar rapidamente a la informacion que necesitas.
                  </Box>
                  <Box className={classes.routeList}>
                    {featuredQuestions.map(item => (
                      <Button
                        key={item.id}
                        variant='outlined'
                        color='primary'
                        endIcon={<ArrowForwardIcon />}
                        className={classes.shortcutButton}
                        onClick={() => openGuide(item.id)}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default Help;
